import React, { useEffect, useId, useRef, useState } from "react";
import { Pagination } from "rsuite";
import { ScaleLoader } from "react-spinners";
import classNames from "classnames";
import AccessibleDialog from "../UI/Modals/AccessibleDialog/AccessibleDialog";
import { fetchItemStockOverview } from "../../services/stockOverviewService";
import {
  ItemStockOverviewResponse,
  StockOverviewRow,
} from "../../types/stockOverview.types";
import { ItemBasicResponse } from "../../types/medicineSearch.types";
import { ItemInMarketplaceBasicResponse } from "../../types/marketplaceItemSearch.types";
import {
  isOwnPharmacyRow,
  stockQuantity,
  toDraftItem,
} from "../../utils/stockOverview";
import { STOCK_OVERVIEW_PAGE_SIZE } from "../../utils/consultOrder.constants";
import { useConsultOrderContext } from "../../contexts/ConsultOrderContext";
import styles from "./ItemStockOverviewModal.module.scss";

// ITAM-2613 — stock overview for a globally searched item, stacked on top of
// the consult-order form modal. Shows the pharmacist's OWN pharmacy as a fixed,
// pinned card (the only one with an add button; synthesized when the pharmacy
// has no stock) above a paginated list of every other pharmacy that stocks the
// item. Adding maps the own stock row into the existing draft contract.

// Square thumbnail size (px); fixed box reserves space before the image loads.
const IMAGE_SIZE = 48;

const CDN_BASE = process.env.REACT_APP_CDN_PATH ?? "";

const firstImageUrl = (item: ItemBasicResponse): string | undefined => {
  const image = item?.files?.find((file) => file.type === "IMAGE");
  return image ? `${CDN_BASE}${image.location}` : undefined;
};

const formatAmount = (value: number): string =>
  `${value.toLocaleString("sr-RS", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} RSD`;

/**
 * Own-pharmacy availability, resolved from the PAGE 0 response. An explicit
 * union — `loading`/`error` must never present the pharmacy as out of stock;
 * "Nema na stanju" is reserved for a CONFIRMED missing row (`unavailable`).
 */
type OwnStockState =
  | { status: "loading" }
  | { status: "available"; row: StockOverviewRow }
  | { status: "unavailable" }
  | { status: "error" };

export interface ItemStockOverviewModalProps {
  /** The dropdown selection (global /search/item result). */
  item: ItemBasicResponse;
  marketplaceId: number;
  branchMarketplaceId: number;
  /** Name for the synthesized zero-stock own card (fallback "Vaša apoteka"). */
  marketplaceName: string | null;
  onAdd: (draftItem: ItemInMarketplaceBasicResponse) => void;
  onClose: () => void;
}

/**
 * Decorative item thumbnail; replaced by a same-size placeholder on load
 * failure so the header keeps its height (no layout shift).
 */
const HeaderImage = ({ src }: { src: string }) => {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <span
        className={styles.headerImageFallback}
        data-testid="stock-overview-image-fallback"
        aria-hidden="true"
      />
    );
  }

  return (
    <img
      className={styles.headerImage}
      src={src}
      alt=""
      width={IMAGE_SIZE}
      height={IMAGE_SIZE}
      onError={() => setFailed(true)}
    />
  );
};

const PriceBlock = ({ row }: { row: StockOverviewRow }) => {
  const hasDiscount = row.itemPriceWithDiscount != null;
  const finalPrice = row.itemPriceWithDiscount ?? row.itemPrice;
  return (
    <span className={styles.price}>
      {hasDiscount && (
        <span className={styles.priceBase}>{formatAmount(row.itemPrice)}</span>
      )}
      <span className={styles.priceFinal}>{formatAmount(finalPrice)}</span>
    </span>
  );
};

const ItemStockOverviewModal: React.FC<ItemStockOverviewModalProps> = ({
  item,
  marketplaceId,
  branchMarketplaceId,
  marketplaceName,
  onAdd,
  onClose,
}) => {
  const { draft } = useConsultOrderContext();

  const [page, setPage] = useState(0);
  const [data, setData] = useState<ItemStockOverviewResponse | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading"
  );
  const [ownState, setOwnState] = useState<OwnStockState>({
    status: "loading",
  });
  // Bumped by "Pokušaj ponovo" to re-fire the current page's fetch.
  const [retryNonce, setRetryNonce] = useState(0);

  const addButtonRef = useRef<HTMLButtonElement>(null);
  const focusedOnceRef = useRef(false);
  const retryButtonRef = useRef<HTMLButtonElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // Set while a retry-initiated fetch is in flight: clicking retry unmounts
  // the focused button (error box -> loading box), so focus must be restored
  // once the fetch settles.
  const pendingRetryFocusRef = useRef(false);

  const reactId = useId();
  const titleId = `stock-overview-${reactId}-title`;

  useEffect(() => {
    const controller = new AbortController();
    setStatus("loading");
    fetchItemStockOverview(
      item.id,
      {
        page,
        priorityMarketplaceId: marketplaceId,
        priorityMarketplaceBranchId: branchMarketplaceId,
      },
      controller.signal
    )
      .then((res) => {
        // Stale-response guard: an aborted request must never change state
        // (a rapid page change would otherwise flip the modal to error).
        if (controller.signal.aborted) {
          return;
        }
        setData(res);
        setStatus("ready");
        if (page === 0) {
          const own = res.marketplaces.find((row) =>
            isOwnPharmacyRow(row, marketplaceId, branchMarketplaceId)
          );
          setOwnState(
            own ? { status: "available", row: own } : { status: "unavailable" }
          );
        }
      })
      .catch(() => {
        if (controller.signal.aborted) {
          return;
        }
        setStatus("error");
        // Only an UNRESOLVED own state degrades to error — a card already
        // captured from a successful page 0 stays through later failures.
        setOwnState((prev) =>
          prev.status === "loading" ? { status: "error" } : prev
        );
      });
    return () => controller.abort();
  }, [item.id, marketplaceId, branchMarketplaceId, page, retryNonce]);

  // react-modal's initialFocusRef fires in onAfterOpen, while the modal is
  // still loading and the add button does not exist yet — so focus is moved
  // here instead, once, when the page-0 fetch confirms the own row (same
  // pattern as the form modal's FORM -> SUCCESS swap). Otherwise the default
  // content focus stays.
  useEffect(() => {
    if (
      !focusedOnceRef.current &&
      ownState.status === "available" &&
      stockQuantity(ownState.row) > 0
    ) {
      focusedOnceRef.current = true;
      addButtonRef.current?.focus();
    }
  }, [ownState]);

  const retry = () => {
    pendingRetryFocusRef.current = true;
    // A page-0 failure parks ownState on "error", which renders the top-level
    // error box regardless of `status` — re-arm it to "loading" so the
    // in-flight retry shows the spinner instead of a stale, click-ignoring
    // error state. Later-page retries (own card already captured) are
    // untouched by the condition.
    setOwnState((prev) =>
      prev.status === "error" ? { status: "loading" } : prev
    );
    setRetryNonce((n) => n + 1);
  };

  // Restore focus after a retry settles: the new retry button on another
  // failure, else the add button when it became focusable, else the dialog's
  // scroll container — never <body> behind an open dialog.
  useEffect(() => {
    if (!pendingRetryFocusRef.current || status === "loading") {
      return;
    }
    pendingRetryFocusRef.current = false;
    if (status === "error") {
      retryButtonRef.current?.focus();
      return;
    }
    if (addButtonRef.current && !addButtonRef.current.disabled) {
      addButtonRef.current.focus();
    } else {
      bodyRef.current?.focus();
    }
  }, [status]);

  const handleAdd = () => {
    if (
      ownState.status !== "available" ||
      stockQuantity(ownState.row) === 0
    ) {
      return;
    }
    onAdd(toDraftItem(ownState.row, item));
    onClose();
  };

  const draftLine = draft.items.find(
    (line) => line.item.id.itemId === item.id
  );

  const imageUrl = firstImageUrl(item);
  const ownRow = ownState.status === "available" ? ownState.row : null;

  const initialLoading = ownState.status === "loading";
  const initialError = ownState.status === "error";

  const otherRows =
    data?.marketplaces.filter(
      (row) => !isOwnPharmacyRow(row, marketplaceId, branchMarketplaceId)
    ) ?? [];

  const loadingBox = (
    <div className={styles.stateBox} role="status" aria-label="Učitavanje">
      <ScaleLoader color="#00b2b1" height={16} width={3} />
    </div>
  );

  const errorBox = (
    <div className={styles.stateBox}>
      <p className={styles.errorText} role="alert">
        Greška pri učitavanju dostupnosti. Pokušajte ponovo.
      </p>
      <button
        type="button"
        ref={retryButtonRef}
        className={styles.retryButton}
        onClick={retry}
      >
        Pokušaj ponovo
      </button>
    </div>
  );

  const renderOwnCard = () => {
    if (ownState.status === "available") {
      const row = ownState.row;
      const stock = stockQuantity(row);
      const stockDropped = draftLine != null && draftLine.quantity > stock;
      return (
        <section
          className={classNames(styles.card, styles.ownCard)}
          aria-label="Vaša apoteka"
        >
          <div className={styles.cardTop}>
            <div className={styles.cardBody}>
              <span className={styles.ownBadge}>Vaša apoteka</span>
              <span className={styles.cardName}>
                {row.marketplaceResponse.name}
                {row.outletMarket && (
                  <span className={styles.outletBadge}>Outlet</span>
                )}
              </span>
              {row.marketplaceResponse.address && (
                <span className={styles.cardMeta}>
                  {row.marketplaceResponse.address}
                </span>
              )}
            </div>
            <div className={styles.cardSide}>
              <span className={styles.stock}>Na stanju: {stock} kom</span>
              <PriceBlock row={row} />
            </div>
          </div>
          {draftLine && (
            <p className={styles.inDraftNote}>
              Već u porudžbini: {draftLine.quantity} kom
            </p>
          )}
          {stockDropped && (
            <p className={styles.stockWarning} role="alert">
              Stanje je promenjeno. Količina u porudžbini biće prilagođena na{" "}
              {stock} kom.
            </p>
          )}
          <div className={styles.cardActions}>
            <button
              type="button"
              ref={addButtonRef}
              className={styles.addButton}
              onClick={handleAdd}
              // Defensive: the backend list filters itemQuantity >= 1, but a
              // fractional 0 < q < 1 row would floor to 0 — never offer an
              // add the order creation would hard-reject.
              disabled={stock === 0}
              aria-describedby={
                stock === 0 ? `${titleId}-no-stock-hint` : undefined
              }
            >
              Dodaj u porudžbinu
            </button>
          </div>
          {stock === 0 && (
            <p
              id={`${titleId}-no-stock-hint`}
              className={styles.noStockHint}
            >
              Artikal nije na stanju u vašoj apoteci — nije moguće dodavanje u
              porudžbinu.
            </p>
          )}
        </section>
      );
    }
    // Confirmed absent row -> synthesized zero-stock card. NEVER rendered for
    // the loading/error states (they use the neutral state boxes instead).
    return (
      <section
        className={classNames(styles.card, styles.ownCard)}
        aria-label="Vaša apoteka"
      >
        <div className={styles.cardTop}>
          <div className={styles.cardBody}>
            <span className={styles.ownBadge}>Vaša apoteka</span>
            <span className={styles.cardName}>
              {marketplaceName ?? "Vaša apoteka"}
            </span>
          </div>
          <div className={styles.cardSide}>
            <span className={styles.noStock}>Nema na stanju</span>
          </div>
        </div>
        {draftLine && (
          <p className={styles.inDraftNote}>
            Već u porudžbini: {draftLine.quantity} kom
          </p>
        )}
        <div className={styles.cardActions}>
          <button
            type="button"
            className={styles.addButton}
            disabled
            aria-describedby={`${titleId}-no-stock-hint`}
          >
            Dodaj u porudžbinu
          </button>
        </div>
        <p id={`${titleId}-no-stock-hint`} className={styles.noStockHint}>
          Artikal nije na stanju u vašoj apoteci — nije moguće dodavanje u
          porudžbinu.
        </p>
      </section>
    );
  };

  const renderOthers = () => {
    if (status === "loading") {
      return loadingBox;
    }
    if (status === "error") {
      return errorBox;
    }
    if (!data) {
      return null;
    }
    if (otherRows.length === 0) {
      if (data.marketplaceCount === 0) {
        return (
          <p className={styles.emptyMessage}>
            Artikal trenutno nije na stanju ni u jednoj apoteci.
          </p>
        );
      }
      if (ownState.status === "available") {
        return (
          <p className={styles.emptyMessage}>
            Nijedna druga apoteka trenutno nema artikal na stanju.
          </p>
        );
      }
      return null;
    }
    return (
      <ul className={styles.cardList} aria-label="Ostale apoteke">
        {otherRows.map((row) => {
          const mp = row.marketplaceResponse;
          return (
            <li
              key={`${mp.marketplaceId}:${mp.branchMarketplaceId}`}
              className={styles.card}
            >
              <div className={styles.cardTop}>
                <div className={styles.cardBody}>
                  <span className={styles.cardName}>
                    {mp.name}
                    {row.outletMarket && (
                      <span className={styles.outletBadge}>Outlet</span>
                    )}
                  </span>
                  {(mp.address || mp.location) && (
                    <span className={styles.cardMeta}>
                      {[mp.address, mp.location].filter(Boolean).join(", ")}
                    </span>
                  )}
                  {mp.phoneNumber && (
                    <span className={styles.cardMeta}>{mp.phoneNumber}</span>
                  )}
                </div>
                <div className={styles.cardSide}>
                  <span className={styles.stock}>
                    Na stanju: {stockQuantity(row)} kom
                  </span>
                  <PriceBlock row={row} />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <AccessibleDialog
      isOpen={true}
      onRequestClose={onClose}
      titleId={titleId}
      maxWidth={680}
      overlayZIndex={1010}
      className={styles.dialogInner}
    >
      <div className={styles.container}>
        <header className={styles.header}>
          {imageUrl && <HeaderImage src={imageUrl} />}
          <div className={styles.headerText}>
            <h2 id={titleId} className={styles.title}>
              {item.name}
            </h2>
            {ownRow?.itemExternalName && (
              <span className={styles.externalName}>
                {ownRow.itemExternalName}
              </span>
            )}
          </div>
        </header>

        <div className={styles.body} ref={bodyRef} tabIndex={-1}>
          {initialLoading ? (
            loadingBox
          ) : initialError ? (
            errorBox
          ) : (
            <>
              {data && data.marketplaceCount > 0 && (
                <p className={styles.totalsLine}>
                  Na stanju u: {data.marketplaceCount} apoteka · Ukupna
                  količina: {Math.floor(data.totalQuantity)} kom
                </p>
              )}

              {renderOwnCard()}

              {renderOthers()}

              {data && data.totalPages > 1 && (
                <div className={styles.pageFooter}>
                  <Pagination
                    total={data.marketplaceCount}
                    limit={STOCK_OVERVIEW_PAGE_SIZE}
                    activePage={page + 1}
                    onChangePage={(nextPage: number) => setPage(nextPage - 1)}
                    prev
                    next
                    maxButtons={7}
                    ellipsis
                    boundaryLinks
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AccessibleDialog>
  );
};

export default ItemStockOverviewModal;
